import makeApiCall from "./master/axiosIndex";

/**
 * Project Cash Entries — the four money heads per project per month that feed
 * the cash-flow report (inflow: RA Bill, Escalation; outflow: Site Expenses,
 * Salaries), plus the project's MAX Infra project link.
 *
 * Natural key on the server: (project, head, month, referenceNo). Saving a NEW
 * entry whose key already exists UPDATES that row ('Entry updated'); editing an
 * entry into a key another row holds is a 400 with a readable message.
 *
 * Amounts travel as strings — parse on the consumer side.
 */

export type CashHead = "RA_BILL" | "ESCALATION" | "SITE_EXPENSE" | "SALARY";

export interface CashEntry {
  cashEntryId: number;
  projectId: number;
  head: CashHead;
  /** 'YYYY-MM-01' */
  entryMonth: string;
  /** 'YYYY-MM-DD' or '' */
  entryDate: string;
  referenceNo: string;
  amount: string;
  remarks: string;
  updatedAt: string;
}

export interface CashEntriesResponse {
  entries: CashEntry[];
  /** '' = not linked */
  maxInfraProjectId: string;
  projectName: string;
}

export interface SaveCashEntryPayload {
  /** omit / 0 to create */
  cashEntryId?: number;
  projectId: number;
  head: CashHead;
  /** 'YYYY-MM' */
  entryMonth: string;
  entryDate?: string;
  referenceNo?: string;
  /** number as string, >= 0 */
  amount: string;
  remarks?: string;
}

/**
 * makeApiCall swallows failures and returns the AxiosError, so the server's
 * readable message lives on error.response.data. Use this at every call site.
 */
export const cashApiErrorMessage = (res: any, fallback: string): string => {
  const raw =
    res?.response?.data?.message ??
    res?.response?.data?.error ??
    (res?.status === false ? res?.message : undefined) ??
    res?.data?.message;
  if (Array.isArray(raw)) return raw.join(", ");
  if (typeof raw === "string" && raw.trim()) return raw;
  return fallback;
};

export const projectCashApis = {
  getEntries: (
    projectId: number | string,
    params?: { head?: CashHead; fromMonth?: string; toMonth?: string }
  ): Promise<any> =>
    makeApiCall({
      url: `/project-cash/entries/${projectId}`,
      method: "get",
      params,
    }),

  saveEntry: (data: SaveCashEntryPayload): Promise<any> =>
    makeApiCall({ url: "/project-cash/entries/save", method: "post", data }),

  deleteEntry: (cashEntryId: number): Promise<any> =>
    makeApiCall({
      url: "/project-cash/entries/delete",
      method: "post",
      data: { cashEntryId },
    }),

  /** '' clears the link. */
  saveMaxInfraLink: (projectId: number, maxInfraProjectId: string): Promise<any> =>
    makeApiCall({
      url: "/project-cash/max-infra-link",
      method: "post",
      data: { projectId, maxInfraProjectId },
    }),

  /**
   * Multipart: `file` + `body` (JSON string `{ projectId, comment? }`).
   * Job type 'project-cash-entries-upload'; then the generic
   * validate → preview → submit/poll flow of ExcelUploadStageModal.
   */
  uploadExcel: (data: FormData): Promise<any> =>
    makeApiCall({ url: "/project-cash/upload/excel", method: "post", data }),
};
